import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../../components/layout/Layout";
import EmployeePicker from "../../../components/ui/EmployeePicker";
import {
  useApprovalPreview,
  useApprovalRuleOptions,
  useApprovalRules,
  useResetApprovalRule,
  useSaveApprovalRule,
} from "../../../api/queries/workflowQueries";
import {
  useCompanies,
  useCompanyRoles,
} from "../../../api/queries/organizationQueries";
import useCurrentUser from "../../../hooks/useCurrentUser";
import usePermissions from "../../../hooks/usePermissions";
import { getApiErrorMessage } from "../../../utils/apiError";
import { formatDateTime } from "../../../utils/dateTime";
import {
  EMPLOYEE_TYPES,
  RULE_TYPES,
  SOURCE,
  choiceKey,
  choiceLabel,
  choicesFor,
  describeLevel,
  fromChoice,
  fromServer,
  inheritedText,
  levelProblems,
  newLevel,
  toPayload,
} from "./approvalRuleFormat";
import EffectiveApprovers from "./EffectiveApprovers";
import ApprovalSetupTabs from "./ApprovalSetupTabs";
import "../admin.css";

const EMPTY = [];
const messagesOf = (err, fallback) => {
  const m = err?.response?.data?.message;
  return Array.isArray(m) ? m : [getApiErrorMessage(err, fallback)];
};
/** Rule employee { id, employeeNumber, name } → picker value. */
const toPicked = (e) =>
  e
    ? {
        id: e.id,
        firstName: e.name,
        lastName: "",
        employeeId: e.employeeNumber,
      }
    : null;

/** One level: a plain choice (who), plus a role, a person or where to look. */
const LevelEditor = ({
  level,
  index,
  count,
  roles,
  companyId,
  allowedTypes,
  onChange,
  onRemove,
  onMove,
  oneStep,
}) => {
  const roleTitle = roles.find((r) => r.id === level.roleId)?.title;
  const set = (changes) => onChange({ ...level, ...changes });
  const choices = choicesFor(allowedTypes, level);
  return (
    <div className="rule-level">
      <div className="rule-level-num" aria-hidden="true">
        {index + 1}
      </div>
      <div className="flex-grow-1 min-w-0">
        <div className="row g-2">
          <div className="col-md-5">
            <label className="profile-label" htmlFor={`lvl-type-${index}`}>
              {oneStep
                ? `Approver ${index + 1}`
                : index === 0
                  ? "First approver"
                  : `Then (level ${index + 1})`}
            </label>
            <select
              id={`lvl-type-${index}`}
              className="form-select form-select-sm"
              value={choiceKey(level)}
              onChange={(e) => onChange(fromChoice(e.target.value, level))}
            >
              {choices.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          {level.type === "ROLE" && (
            <div className="col-md-7">
              <label className="profile-label" htmlFor={`lvl-role-${index}`}>
                Job role
              </label>
              <select
                id={`lvl-role-${index}`}
                className="form-select form-select-sm"
                value={level.roleId}
                onChange={(e) => set({ roleId: e.target.value })}
              >
                <option value="">Choose a role…</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.title}
                  </option>
                ))}
              </select>
            </div>
          )}
          {level.type === "EMPLOYEE" && (
            <div className="col-md-7">
              <label className="profile-label" htmlFor={`lvl-emp-${index}`}>
                Person
              </label>
              <EmployeePicker
                id={`lvl-emp-${index}`}
                companyId={companyId}
                value={toPicked(level.employee)}
                onChange={(e) =>
                  set({
                    employeeId: e?.id || "",
                    employee: e
                      ? {
                          id: e.id,
                          name: `${e.firstName || ""} ${e.lastName || ""}`.trim(),
                          employeeNumber: e.employeeId,
                        }
                      : null,
                  })
                }
              />
            </div>
          )}
        </div>
        {(level.type === "ROLE" || level.type === "ROLE_SUPERVISOR") && (
          <div className="form-check mt-2">
            <input
              id={`lvl-search-${index}`}
              type="checkbox"
              className="form-check-input"
              checked={level.search === "BRANCH_TREE"}
              onChange={(e) =>
                set({ search: e.target.checked ? "BRANCH_TREE" : "COMPANY" })
              }
            />
            <label
              className="form-check-label fs-080"
              htmlFor={`lvl-search-${index}`}
            >
              Only people in the employee's branch or the branches above it
            </label>
          </div>
        )}
        <div className="fs-075 text-slate-500 mt-1">
          <i className="bi bi-arrow-return-right me-1"></i>
          {describeLevel(level, roleTitle)}
        </div>
      </div>
      <div className="row-tools">
        <button
          type="button"
          className="btn btn-sm btn-light"
          onClick={() => onMove(-1)}
          disabled={index === 0}
          aria-label="Move up"
        >
          <i className="bi bi-arrow-up"></i>
        </button>
        <button
          type="button"
          className="btn btn-sm btn-light"
          onClick={() => onMove(1)}
          disabled={index === count - 1}
          aria-label="Move down"
        >
          <i className="bi bi-arrow-down"></i>
        </button>
        <button
          type="button"
          className="btn btn-sm btn-light text-danger"
          onClick={onRemove}
          aria-label={`Remove level ${index + 1}`}
        >
          <i className="bi bi-trash"></i>
        </button>
      </div>
    </div>
  );
};

/** One request type: current rule (read) or the level editor (edit). */
const RuleCard = ({
  row,
  companyId,
  employeeType,
  options,
  roles,
  canEdit,
}) => {
  const [levels, setLevels] = useState(null); // null = not editing
  const [errors, setErrors] = useState([]);
  const save = useSaveApprovalRule();
  const reset = useResetApprovalRule();
  const busy = save.isPending || reset.isPending;
  const max = row.multiLevel === false ? 1 : options.maxLevels || 5;
  const allowedTypes = options.ruleTypes?.length
    ? options.ruleTypes.map((t) => t.type)
    : Object.keys(RULE_TYPES);
  const source = SOURCE[row.source] || { label: row.source, tone: "gray" };

  const startEdit = () => {
    setErrors([]);
    setLevels(row.levels?.length ? fromServer(row.levels) : [newLevel()]);
  };
  const move = (i, dir) =>
    setLevels((l) => {
      const next = [...l];
      [next[i], next[i + dir]] = [next[i + dir], next[i]];
      return next;
    });

  const submit = () => {
    const problems = levelProblems(levels);
    if (!levels.length)
      problems.push(
        "Add at least one approver, or use the parent/default rule instead.",
      );
    if (problems.length) return setErrors(problems);
    setErrors([]);
    save.mutate(
      {
        companyId,
        type: row.requestType,
        levels: toPayload(levels),
        employeeType: employeeType || undefined,
      },
      {
        onSuccess: () => {
          toast.success(`${row.label} rule saved.`);
          setLevels(null);
        },
        onError: (err) =>
          setErrors(messagesOf(err, "Could not save the rule.")),
      },
    );
  };
  const doReset = () => {
    if (
      !window.confirm(
        employeeType
          ? `Remove this company's ${row.label} rule for ${employeeType.toLowerCase()} employees? They go back to the all-employees rule.`
          : `Remove this company's ${row.label} rule? It goes back to the parent company's rule or the default.`,
      )
    )
      return;
    reset.mutate(
      {
        companyId,
        type: row.requestType,
        employeeType: employeeType || undefined,
      },
      {
        onSuccess: () => {
          toast.success(
            employeeType
              ? `${row.label} for ${employeeType.toLowerCase()} employees now uses the all-employees rule.`
              : `${row.label} now uses the parent/default rule.`,
          );
          setLevels(null);
        },
        onError: (err) =>
          toast.error(getApiErrorMessage(err, "Could not reset the rule.")),
      },
    );
  };

  return (
    <section className="admin-card rule-card">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
        <div>
          <h2 className="admin-card-title m-0">
            {row.label || row.requestType}
          </h2>
          <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
            <span className={`status-pill status-pill--${source.tone}`}>
              {inheritedText(row, companyId) ||
                (employeeType && row.source === "COMPANY"
                  ? `${employeeType} employees rule`
                  : source.label)}
            </span>
            <span className="fs-075 text-slate-500">
              {row.multiLevel === false
                ? "1 approver"
                : row.oneStep
                  ? `Up to ${max} approvers · any one decides`
                  : `Up to ${max} approvers in order`}
            </span>
            {row.source === "COMPANY" && row.updatedAt && (
              <span className="fs-070 text-slate-400">
                Updated {formatDateTime(row.updatedAt)}
              </span>
            )}
          </div>
        </div>
        {canEdit && !levels && (
          <div className="d-flex gap-2">
            {row.source === "COMPANY" && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={doReset}
                disabled={busy}
              >
                {employeeType
                  ? "Use the all-employees rule"
                  : "Use parent/default"}
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={startEdit}
            >
              <i className="bi bi-pencil me-1"></i>
              {row.source === "COMPANY" ? "Change" : "Set who approves"}
            </button>
          </div>
        )}
      </div>

      {!levels &&
        (row.source === "DEFAULT" || !(row.levels || EMPTY).length ? (
          <p className="fs-080 text-slate-500 mb-0">
            Not set for this company: their branch manager approves (one level).
            HR can always approve.
          </p>
        ) : (
          <ol
            className="rule-levels-read"
            aria-label={row.oneStep ? "Approvers: any one decides" : undefined}
          >
            {row.oneStep && (
              <li className="list-unstyled fs-075 text-slate-500">
                Approvers (any one decides; the first decision is final):
              </li>
            )}
            {row.levels.map((l) => (
              <li key={l.level}>
                <span className="fw-semibold">{choiceLabel(l)}</span>
                <span className="text-slate-600">
                  : {l.description || describeLevel(l)}
                </span>
              </li>
            ))}
          </ol>
        ))}

      {levels && (
        <>
          {errors.length > 0 && (
            <div className="alert alert-danger fs-085 py-2">
              {errors.length === 1 ? (
                errors[0]
              ) : (
                <ul className="mb-0 ps-3">
                  {errors.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div className="d-flex flex-column gap-2">
            {levels.map((l, i) => (
              <LevelEditor
                key={i}
                level={l}
                index={i}
                oneStep={row.oneStep}
                count={levels.length}
                roles={roles}
                companyId={companyId}
                allowedTypes={allowedTypes}
                onChange={(next) =>
                  setLevels((list) => list.map((x, j) => (j === i ? next : x)))
                }
                onRemove={() =>
                  setLevels((list) => list.filter((_, j) => j !== i))
                }
                onMove={(dir) => move(i, dir)}
              />
            ))}
          </div>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
            <button
              type="button"
              className="btn btn-sm btn-light border"
              onClick={() => setLevels((l) => [...l, newLevel()])}
              disabled={levels.length >= max}
            >
              <i className="bi bi-plus-lg me-1"></i>Add another approver{" "}
              {levels.length >= max ? `(max ${max})` : ""}
            </button>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary profile-btn"
                onClick={() => setLevels(null)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary profile-btn"
                onClick={submit}
                disabled={busy}
              >
                {save.isPending && (
                  <span
                    className="spinner-border spinner-border-sm"
                    aria-hidden="true"
                  ></span>
                )}
                Save
              </button>
            </div>
          </div>
          <p className="fs-070 text-slate-500 mt-2 mb-0">
            {row.oneStep
              ? "Everyone listed can decide, and the first approve or reject is final. A branch manager above the employee and HR can decide too. The employee is never their own approver."
              : "Approvers go in order: level 1 first, then level 2 once level 1 approves. A level with nobody in it is skipped, and the employee is never their own approver."}
          </p>
        </>
      )}
    </section>
  );
};

/** Admins/HR: who approves DTR, overtime, leave and check-in validation, per company. */
const ApprovalRulesPage = () => {
  const user = useCurrentUser();
  const { companyWide } = usePermissions();
  const canEdit = companyWide("company-setup", "modify");
  const { data: companies = EMPTY } = useCompanies();
  const [companyId, setCompanyId] = useState(user.companyId || "");
  const activeCompany = companyId || user.companyId || "";
  const optionsQuery = useApprovalRuleOptions();
  // "" = all employees; Internal / Deployed = rules for that type only
  const [employeeType, setEmployeeType] = useState("");
  const rulesQuery = useApprovalRules(activeCompany, employeeType || undefined);
  // Shared roles + this company's roles (filtered by the server)
  const { data: companyRoles = EMPTY } = useCompanyRoles(activeCompany);
  const roles = useMemo(
    () => companyRoles.filter((r) => r.isActive !== false),
    [companyRoles],
  );
  const [testEmployee, setTestEmployee] = useState(null);
  const preview = useApprovalPreview(testEmployee?.id);

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="mb-3">
          <Link
            to="/admin"
            className="text-slate-500 fs-080 text-decoration-none"
          >
            <i className="bi bi-arrow-left"></i> Administration
          </Link>
          <h1 className="fw-bold text-dark m-0 page-title">Approval Setup</h1>
          <p className="text-muted small mb-0">
            Who approves DTR, overtime, leave and the other requests.
          </p>
        </div>

        <ApprovalSetupTabs />

        {companies.length > 1 && (
          <div className="mb-3 d-flex align-items-center gap-2">
            <label htmlFor="rules-company" className="fs-080 text-slate-600">
              Company
            </label>
            <select
              id="rules-company"
              className="status-select"
              value={activeCompany}
              onChange={(e) => {
                setCompanyId(e.target.value);
                setTestEmployee(null);
              }}
            >
              {!activeCompany && <option value="">Choose a company…</option>}
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.companyName || c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {!activeCompany ? (
          <div className="admin-empty">
            <i className="bi bi-buildings"></i>Choose a company first.
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            <section className="admin-card rule-test">
              <h2 className="admin-card-title">
                <i className="bi bi-person-check"></i>Check who approves an
                employee
              </h2>
              <p className="fs-075 text-slate-500">
                Pick an employee to see who approves each of their requests
                right now, with all the settings below applied.
              </p>
              <EmployeePicker
                companyId={activeCompany}
                value={testEmployee}
                onChange={setTestEmployee}
                placeholder="Search employee name"
              />
              <div className="mt-3">
                {preview.isFetching && !preview.data && (
                  <div className="text-center py-3">
                    <span
                      className="spinner-border spinner-border-sm text-primary"
                      role="status"
                      aria-label="Loading"
                    ></span>
                  </div>
                )}
                {preview.isError && (
                  <div className="alert alert-danger fs-080 py-2">
                    {getApiErrorMessage(
                      preview.error,
                      "Could not load the preview.",
                    )}
                  </div>
                )}
                {testEmployee && preview.data && (
                  <EffectiveApprovers
                    byType={preview.data.byType}
                    chainFor={`${testEmployee.firstName || ""} ${testEmployee.lastName || ""}`.trim()}
                  />
                )}
              </div>
            </section>
            <div>
              <h2 className="fs-6 fw-bold text-navy mb-1">
                Company rules, per request
              </h2>
              <p className="fs-075 text-slate-500 mb-2">
                Used for every employee in this company who has no approvers of
                their own. Internal and deployed employees can have their own
                rules; without one they use the all-employees rule.
              </p>
              <div
                className="approval-subtabs mb-3"
                role="tablist"
                aria-label="Employee type"
              >
                {EMPLOYEE_TYPES.map((t) => (
                  <button
                    key={t.value || "all"}
                    type="button"
                    role="tab"
                    aria-selected={employeeType === t.value}
                    className={`approval-subtab ${employeeType === t.value ? "active" : ""}`}
                    onClick={() => setEmployeeType(t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {(rulesQuery.isError || optionsQuery.isError) && (
                <div className="alert alert-danger fs-085">
                  {getApiErrorMessage(
                    rulesQuery.error || optionsQuery.error,
                    "Could not load the rules.",
                  )}
                </div>
              )}
              {rulesQuery.isLoading && (
                <div className="text-center py-5">
                  <span
                    className="spinner-border text-primary"
                    role="status"
                    aria-label="Loading"
                  ></span>
                </div>
              )}
              <div className="d-flex flex-column gap-3">
                {(rulesQuery.data?.data || EMPTY).map((row) => (
                  <RuleCard
                    key={`${activeCompany}-${employeeType}-${row.requestType}`}
                    row={row}
                    companyId={activeCompany}
                    employeeType={employeeType}
                    options={optionsQuery.data || {}}
                    roles={roles}
                    canEdit={canEdit}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ApprovalRulesPage;
